import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { PAGE_META } from '../../lib/seoMeta';
import { Wand2, Clock, Euro, Camera } from 'lucide-react';
import Breadcrumbs from '../Breadcrumbs';
import PhotoUpload from './PhotoUpload';
import DesignOptions from './DesignOptions';
import ResultView from './ResultView';
import LeadForm from './LeadForm';
import { useVisualizer } from './useVisualizer';

const STEPS = [
  { icon: Camera, title: 'Upload uw foto', text: 'Badkamer, keuken, woonkamer, zolder… één foto volstaat.' },
  { icon: Wand2, title: 'Typ wat u wil', text: 'In uw eigen woorden, zoals aan ChatGPT. Of kies kleur, materiaal en vloer.' },
  { icon: Clock, title: 'Resultaat in ±1 minuut', text: 'Fotorealistisch ontwerp van úw ruimte, 3 varianten gratis.' },
  { icon: Euro, title: 'Vraag uw prijs aan', text: 'Ontwerp per e-mail + gratis plaatsbezoek binnen 48u, vrijblijvend uitgewerkt door onze specialisten.' },
];

const VisualizerPage = () => {
  const vz = useVisualizer();
  const [room, setRoom] = useState(null);
  const [choices, setChoices] = useState({});
  const [extra, setExtra] = useState('');
  const [favorite, setFavorite] = useState(null);
  const done = useMemo(() => vz.session?.generations?.filter((g) => g.status === 'done') || [], [vz.session]);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (done.length && !done.some((g) => g.id === favorite)) setFavorite(done[done.length - 1].id); }, [done, favorite]);

  const onGenerate = () => vz.generate(room, choices, extra, done.length ? (favorite || done[done.length - 1].id) : null);

  return (
    <div data-testid="visualizer-page">
      <Helmet>
        <title>{PAGE_META.visualizer.title}</title>
        <meta name="description" content={PAGE_META.visualizer.description} />
        <link rel="canonical" href="https://maxq.be/renovatie-ontwerpen" />
      </Helmet>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Renovatie ontwerpen met AI' }]} />

      <section className="bg-[#f4efe9] py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#3a190b]/70 mb-4"><Wand2 className="h-4 w-4" />Gratis AI-renovatieontwerp</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#202020] leading-[1.05] mb-5">Zie uw gerenoveerde ruimte vóór u beslist.</h1>
            <p className="text-base md:text-lg text-[#202020]/80 max-w-xl">Upload een foto van uw badkamer, keuken, woonkamer of zolder en beschrijf in uw eigen woorden wat u wil. Onze AI toont in één minuut een fotorealistisch ontwerp van úw ruimte. Daarna maken wij het écht — met eigen vakmensen uit Ham.</p>
          </div>
          <ol className="grid sm:grid-cols-2 gap-3">
            {STEPS.map((s, i) => (
              <li key={s.title} className="bg-white rounded-xl p-4 border border-[#3a190b]/10">
                <div className="flex items-center gap-2 mb-1.5"><span className="w-6 h-6 rounded-full bg-[#3a190b] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span><s.icon className="h-4 w-4 text-[#3a190b]" /></div>
                <p className="font-semibold text-sm text-[#202020]">{s.title}</p>
                <p className="text-xs text-[#202020]/65">{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 space-y-10">
          {vz.error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3" data-testid="visualizer-error">{vz.error}</p>}
          {!vz.session ? (
            <PhotoUpload onFile={vz.upload} busy={vz.busy} />
          ) : (
            <>
              <ResultView session={vz.session} favorite={favorite} setFavorite={setFavorite} onSessionUpdate={vz.applySession} onRequestQuote={() => document.getElementById('visualizer-lead-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
              {vz.session.remaining > 0 ? (
                <div className="rounded-2xl border border-[#3a190b]/10 bg-[#faf7f5] p-6 md:p-8">
                  <DesignOptions options={vz.options} room={room} setRoom={setRoom} choices={choices} setChoices={setChoices} extra={extra} setExtra={setExtra}
                    onGenerate={onGenerate} busy={vz.busy} pending={vz.pending} remaining={vz.session.remaining}
                    session={vz.session} onAddReference={vz.addReference} refining={done.length > 0} />
                </div>
              ) : (
                <p className="text-sm text-[#202020]/70 bg-[#faf7f5] rounded-xl p-4" data-testid="limit-reached">U heeft uw 3 gratis ontwerpen gebruikt. Vraag hieronder uw gratis plaatsbezoek aan — we werken uw favoriete ontwerp samen verder uit.</p>
              )}
              <LeadForm submitLead={vz.submitLead} favorite={favorite} hasDesign={done.length > 0} />
              <button type="button" onClick={vz.reset} className="text-xs text-[#202020]/50 underline" data-testid="restart-btn">Opnieuw beginnen met een andere foto</button>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default VisualizerPage;
